/**
 * Trend Showcase — 3-level tabbed product carousel.
 *
 * Level 1 tabs swap panel groups, level 2 pills swap category bodies,
 * level 3 pills swap product panels. Panels past the first one ship their
 * markup inside a <template> and are hydrated the first time they are shown,
 * so a section with many tabs still starts with a light DOM.
 */
(function () {
  'use strict';

  if (customElements.get('trend-showcase')) return;

  var SCROLL_RATIO = 0.85;

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  class TrendShowcase extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.tabs = toArray(this.querySelectorAll('[data-ts-tab]'));
      this.groups = toArray(this.querySelectorAll('[data-ts-group]'));

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      this._refresh = function () {
        if (this._frame) return;
        this._frame = requestAnimationFrame(
          function () {
            this._frame = null;
            this.refreshStrips();
          }.bind(this)
        );
      }.bind(this);
      window.addEventListener('resize', this._refresh);

      if ('ResizeObserver' in window) {
        this._observer = new ResizeObserver(this._refresh);
        this._observer.observe(this);
      }

      toArray(this.querySelectorAll('[data-ts-track]')).forEach(
        function (track) {
          track.addEventListener('scroll', this._refresh, { passive: true });
        }.bind(this)
      );

      // Hydrate whatever is on screen at first paint, then size the arrows.
      this.hydrateVisible();
      this.refreshStrips();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._refresh);
      if (this._observer) this._observer.disconnect();
    }

    /* ---------------------------------------------------------------- events */

    onClick(event) {
      var arrow = event.target.closest('[data-ts-scroll]');
      if (arrow && this.contains(arrow)) {
        this.scrollStrip(arrow);
        return;
      }

      var tab = event.target.closest('[data-ts-tab]');
      if (tab && this.contains(tab)) {
        this.selectTab(tab.getAttribute('data-ts-tab'));
        return;
      }

      var cat = event.target.closest('[data-ts-cat]');
      if (cat && this.contains(cat)) {
        this.selectCategory(cat.getAttribute('data-ts-cat'));
        return;
      }

      var panelPill = event.target.closest('[data-ts-panel]');
      if (panelPill && this.contains(panelPill)) {
        this.selectPanel(panelPill.getAttribute('data-ts-panel'));
      }
    }

    onKeydown(event) {
      var tab = event.target.closest('[data-ts-tab]');
      if (!tab) return;

      var keys = { ArrowLeft: -1, ArrowRight: 1, Home: 'first', End: 'last' };
      var move = keys[event.key];
      if (move === undefined) return;

      event.preventDefault();

      var index = this.tabs.indexOf(tab);
      var next;
      if (move === 'first') next = 0;
      else if (move === 'last') next = this.tabs.length - 1;
      else next = (index + move + this.tabs.length) % this.tabs.length;

      var target = this.tabs[next];
      if (!target) return;
      this.selectTab(target.getAttribute('data-ts-tab'));
      target.focus();
    }

    /* -------------------------------------------------------------- selection */

    selectTab(id) {
      if (!id) return;

      this.tabs.forEach(function (tab) {
        var active = tab.getAttribute('data-ts-tab') === id;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });

      this.groups.forEach(function (group) {
        group.hidden = group.getAttribute('data-ts-group') !== id;
      });

      this.hydrateVisible();
      this.refreshStrips();
    }

    selectCategory(id) {
      if (!id) return;

      var group = this.querySelector('[data-ts-catbody="' + id + '"]');
      group = group && group.closest('[data-ts-group]');
      if (!group) return;

      toArray(group.querySelectorAll('[data-ts-cat]')).forEach(function (pill) {
        var active = pill.getAttribute('data-ts-cat') === id;
        pill.classList.toggle('is-active', active);
        pill.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      toArray(group.querySelectorAll('[data-ts-catbody]')).forEach(function (body) {
        body.hidden = body.getAttribute('data-ts-catbody') !== id;
      });

      this.hydrateVisible();
      this.refreshStrips();
    }

    selectPanel(id) {
      if (!id) return;

      var panel = this.querySelector('[data-ts-panelid="' + id + '"]');
      var body = panel && panel.closest('[data-ts-catbody]');
      if (!body) return;

      toArray(body.querySelectorAll('[data-ts-panel]')).forEach(function (pill) {
        var active = pill.getAttribute('data-ts-panel') === id;
        pill.classList.toggle('is-active', active);
        pill.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      toArray(body.querySelectorAll('[data-ts-panelid]')).forEach(function (item) {
        item.hidden = item.getAttribute('data-ts-panelid') !== id;
      });

      this.hydrate(panel);
      this.refreshStrips();
    }

    /** Activate the whole tab → category → panel chain that contains `node`. */
    reveal(node) {
      if (!node) return;

      var group = node.closest('[data-ts-group]');
      if (group) this.selectTab(group.getAttribute('data-ts-group'));

      // A level 2 pill names its category directly; a level 3 pill sits inside it.
      var catId = node.getAttribute('data-ts-cat');
      if (!catId) {
        var body = node.closest('[data-ts-catbody]');
        if (body) catId = body.getAttribute('data-ts-catbody');
      }
      if (catId) this.selectCategory(catId);

      var panelId = node.getAttribute('data-ts-panel');
      if (panelId) this.selectPanel(panelId);
    }

    /* -------------------------------------------------------------- rendering */

    hydrate(panel) {
      if (!panel) return;
      var template = panel.querySelector('template[data-ts-defer]');
      if (!template) return;
      panel.replaceChild(template.content.cloneNode(true), template);
      toArray(panel.querySelectorAll('[data-ts-track]')).forEach(
        function (track) {
          track.addEventListener('scroll', this._refresh, { passive: true });
        }.bind(this)
      );
    }

    hydrateVisible() {
      toArray(this.querySelectorAll('.ts-panel:not([hidden])')).forEach(function (panel) {
        if (panel.offsetParent !== null || !panel.closest('[hidden]')) this.hydrate(panel);
      }, this);
    }

    /* ---------------------------------------------------------------- scrolling */

    scrollStrip(button) {
      var strip = button.closest('[data-ts-strip]');
      var track = strip && strip.querySelector('[data-ts-track]');
      if (!track) return;

      var direction = parseInt(button.getAttribute('data-ts-scroll'), 10) || 1;
      track.scrollBy({
        left: direction * track.clientWidth * SCROLL_RATIO,
        behavior: 'smooth'
      });
    }

    refreshStrips() {
      toArray(this.querySelectorAll('[data-ts-strip]')).forEach(function (strip) {
        var track = strip.querySelector('[data-ts-track]');
        var prev = strip.querySelector('[data-ts-scroll="-1"]');
        var next = strip.querySelector('[data-ts-scroll="1"]');
        if (!track || !prev || !next) return;

        // A hidden strip reports zero width; leave its arrows alone until shown.
        if (!track.clientWidth) return;

        var max = track.scrollWidth - track.clientWidth;
        var overflowing = max > 2;

        prev.hidden = !overflowing || track.scrollLeft <= 2;
        next.hidden = !overflowing || track.scrollLeft >= max - 2;
      });
    }
  }

  customElements.define('trend-showcase', TrendShowcase);

  /* ------------------------------------------------------- theme editor hooks */

  function editorTarget(section, blockId) {
    return (
      section.querySelector('[data-ts-tab="' + blockId + '"]') ||
      section.querySelector('[data-ts-cat="' + blockId + '"]') ||
      section.querySelector('[data-ts-block="' + blockId + '"]')
    );
  }

  document.addEventListener('shopify:block:select', function (event) {
    var section = event.target.querySelector('trend-showcase') || event.target.closest('trend-showcase');
    if (!section) return;

    var node = editorTarget(section, event.detail.blockId);
    if (!node) return;

    if (node.hasAttribute('data-ts-tab')) section.selectTab(node.getAttribute('data-ts-tab'));
    else section.reveal(node);
  });

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('trend-showcase');
    if (section && typeof section.refreshStrips === 'function') section.refreshStrips();
  });
})();
